import { Component, EventEmitter, Input, OnChanges, OnInit, Output, ViewChild } from '@angular/core';
import { ButtonType, DateUtils, ModalComponent, TextCase } from '@yupma/ui-kit';
import { ActivityDefinitionDocument, ActivityDocument, ActivityField, ActivityType, EntityAddFormField, EntityType, FieldType, UserDocument } from 'server-model';
import { CRMActivityDataService, IAMService, UIService } from 'src/app/services/services.module';

class UIActivityField {
  constructor(
    public name: string,
    public label: string,
    public description: string,
    public field_type: FieldType | null,
    public is_readonly_definition: boolean,
    public is_readonly_value: boolean,
    public required: boolean,
    public isInvalid: boolean = false, // Defaults to no error
    public errorText: string = "", // Defaults to no error message
    public option: any[] = []
  ) { }
}

@Component({
  selector: 'app-create-activity-modal',
  templateUrl: './create-activity-modal.component.html',
  styleUrl: './create-activity-modal.component.scss'
})
export class CreateActivityModalComponent implements OnChanges {
  @Output() onClose = new EventEmitter();
  @Output() onSave = new EventEmitter<ActivityDocument>();

  @ViewChild(ModalComponent) modalComponent!: ModalComponent;
  @Input() allActivitiesDefinitionDocument: ActivityDefinitionDocument[] = [];
  @Input() allUsers: UserDocument[] = [];
  @Input() currentActivityType: ActivityType | string = '';
  @Input() entityType: EntityType | null = null;
  @Input() entityId: string = '';
  @Input() isLog: boolean = false;

  textCases = TextCase;

  isLoading: boolean = false;
  buttonTypes = ButtonType;
  uiFields: UIActivityField[] = [];
  fieldTypes = FieldType;
  activityDocument: ActivityDocument | null = null;
  currentActivityDefinition: ActivityDefinitionDocument | null = null;

  constructor(
    private uiService: UIService,
    private iamService: IAMService,
    private crmActivityDataService: CRMActivityDataService,
  ) { }

  ngOnChanges() {
    this.initializeActivityDefinition();
    this.initializeUIFields();
  }

  closeModal() {
    this.modalComponent.closeModal();
    setTimeout(() => {
      this.uiService.closeAddActivityModal();
    });
  }

  private initializeActivityDefinition(): void {
    this.currentActivityDefinition = this.allActivitiesDefinitionDocument.find(
      definition => definition.name === this.currentActivityType
    ) || null;
  }

  private initializeUIFields(): void {
    this.activityDocument = new ActivityDocument({
      id: "",
      org_id: this.iamService.getOrganisationId(),
      type: this.currentActivityType,
      related_entities: {},
    });

    // If entityType and entityId are provided, set related_entities
    if (this.entityType && this.entityId) {
      this.activityDocument.related_entities[this.entityType] = [this.entityId];
    }
    if (this.isLog) {
      this.activityDocument['is_log'] = this.isLog;
    }

    if (this.currentActivityDefinition) {
      this.uiFields = this.currentActivityDefinition.fields.map(field => {
        return new UIActivityField(
          field.name,
          field.label,
          field.description,
          field.field_type,
          field.is_readonly_definition,
          field.is_readonly_value,
          field.required,
          false,
          "",
          field.option || []
        );
      });
    }
  }

  inputValueChange(value: any, field: UIActivityField) {
    field.isInvalid = false;
    field.errorText = "";

    // Update the activityDocument dynamically
    if (this.activityDocument) {
      this.activityDocument[field.name] = value;
    }

    // Validate Event type fields after value change
    if (this.currentActivityType === ActivityType.EVENT) {
      this.validateEventFields();
    }
  }

  private validateEventFields(): void {
    if (!this.activityDocument) return;

    const fromField = this.uiFields.find(field => field.name === 'from');
    const toField = this.uiFields.find(field => field.name === 'to');

    if (fromField && toField && this.activityDocument['from'] && this.activityDocument['to']) {
      const fromValue = new Date(this.activityDocument['from']);
      const toValue = new Date(this.activityDocument['to']);

      // Reset previous validation errors for these fields
      toField.isInvalid = false;
      toField.errorText = "";

      // Check if 'to' is less than 'from'
      if (toValue < fromValue) {
        toField.isInvalid = true;
        toField.errorText = "End time cannot be earlier than start time";
      }
    }
  }

  async saveActivity() {
    try {
      if (this.activityDocument) {
        let hasErrors = false;
        this.uiFields.forEach(field => {
          // Check for required fields
          if (field.required && (!this.activityDocument![field.name] || this.activityDocument![field.name].length === 0) && !field.isInvalid) {
            field.isInvalid = true;
            field.errorText = "This field is required.";
            hasErrors = true;
          }

          // Check for invalid fields
          if (field.isInvalid) {
            hasErrors = true;
          }
        });

        // Additional validation for Event type
        if (this.currentActivityType === ActivityType.EVENT) {
          this.validateEventFields();
          // Check if validation added any errors
          this.uiFields.forEach(field => {
            if (field.isInvalid) {
              hasErrors = true;
            }
          });
        }

        if (!hasErrors) {
          this.isLoading = true;
          const uid = this.iamService.getUserId();
          const currentDate = DateUtils.getCurrentTimeStamp();

          // Set default fields
          this.activityDocument['source'] = 'CRM ui';
          this.activityDocument['source_user_agent'] = this.uiService.getBrowserInfo();
          this.activityDocument['created_on'] = currentDate;
          this.activityDocument['created_by'] = uid;
          this.activityDocument['updated_on'] = currentDate;
          this.activityDocument['updated_by'] = uid;

          let savedActivity = (await this.crmActivityDataService.createActivity(this.activityDocument)).data;
          this.uiService.showToast('Activity created');
          this.isLoading = false;
          this.uiService.activityAdded(savedActivity);

          this.closeModal();
        }
      }
    } catch (error) {
      this.isLoading = false;
      console.log(error);
    }
  }

  invalidateField(isInvalid: boolean, field: UIActivityField) {
    if (isInvalid) {
      field.isInvalid = true;
      field.errorText = '';
    } else {
      field.isInvalid = false;
      field.errorText = '';
    }
  }
}